/**
 * Functions の画面（fn.*）と X の共有の画面（share.*・error.share_*）の文言。
 * 2026-09-28 に Money の辞書（ja.money.ts）から移した（Money の了承・お金の画面ではないため Creator の持ち物）。
 * 言葉は founder の方針（MASTER-PLAN §0）に合わせて「プロジェクト / アプリ」「利用者」。
 */
import type { enFnShare } from './en.fnShare';

type FnShareMessages = { [K in keyof typeof enFnShare]: string };

export const jaFnShare: FnShareMessages = {
  'fn.title': 'Functions',
  'fn.lead': '自分で書いたサーバーのプログラムを、専用のデータベースと一緒にアプリの隣で動かせます。前払い残高から原価で差し引きます。',
  'fn.state.on': 'オン',
  'fn.state.off': 'オフ',
  'fn.state.paused': '停止中',
  'fn.state.unavailable': 'このサーバーでは使えません',
  'fn.unavailableTitle': 'この{product}サーバーではFunctionsを動かせません。',
  'fn.unavailableBody': '手元のサーバーには通常Functionsの実行基盤がありません。ほかの機能はそのまま使えます。',
  'fn.offBody': '{env}のFunctionsはオフです。ここか{cmd}でオンにできます。',
  'fn.pausedTitle': '前払い残高がなくなったため停止しています。',
  'fn.pausedBody': '入金するまで、サーバーを使う機能は動きません。サーバーの中身とデータは残っています。',
  'fn.turnOn': 'オンにする',
  'fn.turnOff': 'オフにする',
  'fn.turnOffTitle': '{env}のFunctionsをオフにしますか？',
  'fn.turnOffBody': 'サーバーを使う機能は動かなくなります（FunctionsのURLを開くと「見つかりません」になります）。中身・データベース・ファイルは残ります。',
  'fn.turnedOn': 'Functionsをオンにしました。',
  'fn.turnedOff': 'Functionsをオフにしました。',
  'fn.url': 'URL',
  'fn.noUrl': 'このサーバーには公開URLがありません。',
  'fn.live': '公開中の版',
  'fn.deployedAgo': '{ago}に更新',
  'fn.notDeployed': 'まだ公開していません。AIに「サーバーを公開して」と頼むか、{cmd}で公開します。',
  'fn.limits': '1回の呼び出しあたりの上限',
  'fn.cpu': '計算に使える時間（ミリ秒）',
  'fn.subRequests': 'ほかのサービスを呼べる回数',
  'fn.limitRange': '（{min}〜{max}）',
  'fn.limitsHint': '止まらなくなった呼び出しを早めに止め、1つの不具合で利用料が膨らまないようにします。',
  'fn.limitsBalance': 'この上限は前払い残高に合わせて上がるため、いまは1回あたり{cap}ミリ秒までです。選んだ{want}ミリ秒をすべて使うには、残高を{balance}以上にしてください。入金の約1分後に自動で上がります。',
  'fn.limitsBalanceLink': '入金する',
  'fn.saveLimits': '上限を保存',
  'fn.limitsSaved': '上限を保存しました。',
  'fn.data': 'データベースと保存先',
  'fn.noData': 'まだ何もつながっていません。AIに「データを保存できるようにして」と頼むと、ここに出ます。',
  'fn.col.binding': '名前',
  'fn.col.kind': '種類',
  'fn.col.created': '作成日時',
  'fn.kind.d1': 'データベース',
  'fn.kind.kv': '名前と値の保存先',
  'fn.kind.r2': 'ファイル置き場',
  'fn.kind.queue': 'あとで処理する列',
  'fn.crons': '定期実行',
  'fn.none': 'なし',
  'fn.deploys': '更新の履歴',
  'fn.noDeploys': 'まだ更新はありません。',
  'fn.col.version': '版',
  'fn.col.message': 'メッセージ',
  'fn.col.size': 'サイズ',
  'fn.col.when': '日時',

  'share.title': 'Xの共有',
  'share.lead': 'どの共有リンクから人が来たか。リンクごとの訪問・新しい利用者・プレイ・売上です。',
  'share.leadNoSales': 'どの共有リンクから人が来たか。リンクごとの訪問・新しい利用者・プレイです。',
  'share.range': '直近30日・UTC',
  'share.visits': '訪問',
  'share.players': '新しい利用者',
  'share.plays': 'プレイ',
  'share.sales': '売上',
  'share.trackingOffTitle': 'このプロジェクトではリンクの記録がオフです。',
  'share.trackingOffBody': 'リンクごとの訪問と新しい利用者を数えるには、configの{key}をtrueにしてください。',
  'share.col.link': 'リンク',
  'share.col.visits': '訪問',
  'share.col.players': '新しい利用者',
  'share.col.plays': 'プレイ',
  'share.col.sales': '売上',
  'share.kind.campaign': '自分のリンク',
  'share.kind.plain': '利用者の共有',
  'share.kind.score': 'スコアの共有',
  'share.kind.rank': '順位の共有',
  'share.kind.challenge': '挑戦状',
  'share.noLabel': '名前なし',
  'share.emptyTitle': 'まだ共有リンクはありません',
  'share.emptyBody': '利用者がアプリから{call}で共有するか、下でキャンペーン用のリンクを作ると、ここに結果が表示されます。',
  'share.newLink': 'キャンペーン用のリンク',
  'share.newLinkLead': '自分の投稿用のリンクを作ると、投稿ごとの成果を比べられます。',
  'share.label': 'ラベル',
  'share.labelPlaceholder': '例: 公開のお知らせ',
  'share.create': 'リンクを作る',
  'share.created': 'リンクを作りました。投稿に貼ってください:',
  'share.createdQuery': 'リンクを作りました。投稿するアプリのURLに{query}を付けてください。',
  'share.linksOff': 'このプロジェクトではキャンペーン用のリンクがオフです。configの{key}をtrueにしてください。',

  'share.tags.title': 'Xのカードのタグ',
  'share.tags.lead': 'このタグを、あなたのアプリのすべてのページの<head>に貼ります。XにURLを投稿すると、このカードが出ます。',
  'share.tags.env': '画像のURLは公開中のアプリのものなので、同じタグがテスト用のアプリでも公開中のアプリでも使えます（書くのは1回）。Xのカードは公開中のアプリのURLで出ます。',
  'share.tags.label': '<head>に貼るタグ',
  'share.tags.image': 'Xに出るカード',
  'share.tags.askLead': 'または、この文をAIエージェントに貼ります:',
  'share.tags.askLabel': 'AIエージェントに頼む文',
  'share.tags.ask': '{product} の X カードのタグを、このアプリのすべてのページの <head> に入れて。タグは `{cmd}` で取って、画像の URL は変えないで。',
  'share.tags.imagesOff': '{feature}をONにすると、ここにタグが出ます。',
  'share.tags.imagesOffWhere': '手元のファイルでONにして反映するか、{link}で切り替えてください。',

  'error.share_links_disabled': 'このプロジェクトではキャンペーン用のリンクがオフです。',
  'error.share_tracking_disabled': 'このプロジェクトではリンクの記録がオフです。',
};
